import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress
} from '@mui/material';
import { useCollections } from '../hooks/useCollections';
import { CollectionInfoTab } from '../components/CollectionInfoTab';
import { CollectionWithRole } from '../types/collection';

export const CollectionSettings: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { fetchCollectionDetail } = useCollections();
  const [collection, setCollection] = useState<CollectionWithRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCollection = async () => {
      if (!collectionId) return;

      setLoading(true);
      setError(null);
      try {
        const data = await fetchCollectionDetail(collectionId);
        setCollection(data);
      } catch (err: unknown) {
        const error = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
        if (error.response?.status === 404) {
          setError('Album not found');
        } else if (error.response?.status === 403) {
          setError('You do not have permission to access this album');
        } else {
          setError(error.response?.data?.message || error.message || 'Failed to load album');
        }
      } finally {
        setLoading(false);
      }
    };

    loadCollection();
  }, [collectionId, fetchCollectionDetail]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !collection) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" gutterBottom>
          {error || 'Album not found'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h1"
        sx={{
          fontSize: '2rem',
          fontFamily: 'Libre Baskerville, serif',
          color: 'primary.main',
          mb: 3
        }}
      >
        Album Settings
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <CollectionInfoTab
            collection={collection}
            onUpdate={(updated) => setCollection(updated)}
            onDelete={() => navigate('/albums')}
          />
        </Box>
      </Paper>
    </Container>
  );
};
